import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export default function CheckoutPIX() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const pedidoData = state?.pedidoData;

  const [cpf, setCPF] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [pixData, setPixData] = useState(null);

  // ----------------- Geração do PIX -----------------
  const gerarPIX = async () => {
    if (!cpf) {
      setError("O CPF é obrigatório.");
      return;
    }

    setStatus("Gerando PIX...");
    setError("");

    try {
      const names = pedidoData.NOME.trim().split(" ");
      const firstName = names[0];
      const lastName = names.length > 1 ? names[names.length - 1] : "N/A";

      const subtotal = pedidoData.SUBTOTAL || pedidoData.produtos.reduce((acc, p) => acc + p.quantidade * p.precoUnitario, 0);
      const frete = pedidoData.FRETE || 0;
      const total = pedidoData.TOTAL || subtotal + frete;
      const externalReference = `${pedidoData.ID_PEDIDO}-Bebilogo Conveniência` || pedidoData.TOKEN;

      const payload = {
        transaction_amount: Number(total),
        description: String(externalReference),
        payment_method_id: "pix",
        payer: {
          email: pedidoData.EMAIL || `cliente${pedidoData.TOKEN}@bebilogo.com.br`,
          first_name: firstName,
          last_name: lastName,
          identification: { type: "CPF", number: cpf.replace(/\D/g, "") },
        },
        external_reference: String(externalReference),
        notification_url: "https://bebilogo.com.br/api-mercadopago/webhook",
        metadata: { pedidoId: String(externalReference), endereco: pedidoData.ENDERECO, frete, subtotal, total },
      };

      const res = await fetch("https://bebilogo.com.br/api-mercadopago/pagamento", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(await res.text());

      const data = await res.json();
      if (data.point_of_interaction?.transaction_data) {
        setPixData(data.point_of_interaction.transaction_data);
        setStatus("PIX gerado com sucesso!");
      } else {
        setError("Erro ao gerar PIX. Tente novamente.");
        setStatus("");
      }
    } catch (err) {
      console.error(err);
      setError(err.message || "Erro ao gerar PIX.");
      setStatus("");
    }
  };

  // ----------------- Polling para aprovação -----------------
  useEffect(() => {
    if (!pedidoData) return; // ✅ checagem segura
    const externalReference = `${pedidoData.ID_PEDIDO}-Bebilogo Conveniência` || pedidoData.TOKEN;

    const interval = setInterval(async () => {
      try {
        const res = await fetch(`https://bebilogo.com.br/api/pedidos/${externalReference}/status`);
        const data = await res.json();

        if (data.status === "approved" || data.status === "APROVADO") {
          clearInterval(interval);
          navigate(`/pedido-confirmado/${externalReference}`);
        }
      } catch (err) {
        console.error("Erro ao checar status do pedido:", err);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [pedidoData, navigate]);

  // ----------------- JSX -----------------
  if (!pedidoData) {
    return <p>Pedido não encontrado.</p>;
  }

  const subtotal = pedidoData.SUBTOTAL || pedidoData.produtos.reduce((acc, p) => acc + p.quantidade * p.precoUnitario, 0);
  const frete = pedidoData.FRETE || 0;
  const total = pedidoData.TOTAL || subtotal + frete;
  const externalReference = `${pedidoData.ID_PEDIDO}-Bebilogo Conveniência` || pedidoData.TOKEN;

  return (
    <div style={{ maxWidth: 600, margin: "20px auto", padding: 20, background: "#fff", borderRadius: 8, boxShadow: "0 0 10px rgba(0,0,0,0.1)" }}>
      <h2>Pagamento via PIX</h2>
      {/* Dados do Cliente */}
      <div style={{ marginBottom: 20 }}>
        <h4>Dados do Cliente</h4>
        <p><strong>Nome:</strong> {pedidoData.NOME}</p>
        <p><strong>Telefone:</strong> {pedidoData.TELEFONE}</p>
        <p><strong>Email:</strong> {pedidoData.EMAIL}</p>
        <p><strong>Endereço:</strong> {pedidoData.ENDERECO}</p>
        <p><strong>ID Pedido:</strong> nº{pedidoData.ID_PEDIDO}-Bebilogo Conveniência</p>
      </div>

      {/* Produtos */}
      <div style={{ border: "1px solid #ccc", borderRadius: 6, padding: 10, marginBottom: 20 }}>
        <h4>Produtos</h4>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #ccc" }}>
              <th style={{ textAlign: "left", padding: 6 }}>Produto</th>
              <th style={{ textAlign: "center", padding: 6 }}>Qtd</th>
              <th style={{ textAlign: "right", padding: 6 }}>Unitário</th>
              <th style={{ textAlign: "right", padding: 6 }}>Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {pedidoData.produtos.map((p, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #eee" }}>
                <td style={{ padding: 6 }}>{p.nome}</td>
                <td style={{ textAlign: "center", padding: 6 }}>{p.quantidade}</td>
                <td style={{ textAlign: "right", padding: 6 }}>R$ {p.precoUnitario.toFixed(2)}</td>
                <td style={{ textAlign: "right", padding: 6 }}>R$ {(p.quantidade * p.precoUnitario).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ marginTop: 10, textAlign: "right" }}>
          <p><strong>Subtotal:</strong> R$ {subtotal.toFixed(2)}</p>
          <p><strong>Frete:</strong> R$ {frete.toFixed(2)}</p>
          <p><strong>Total:</strong> R$ {total.toFixed(2)}</p>
        </div>
      </div>

      {/* CPF e botão */}
      <input
        type="text"
        placeholder="Digite o CPF para gerar PIX"
        value={cpf}
        onChange={(e) => setCPF(e.target.value)}
        style={{ width: "100%", padding: 8, marginBottom: 12 }}
      />
      <button
        onClick={gerarPIX}
        style={{ width: "100%", padding: 10, backgroundColor: "#1890ff", color: "#fff", border: "none", borderRadius: 4, fontWeight: "bold", cursor: "pointer" }}
      >
        Gerar PIX
      </button>

      {/* PIX Gerado */}
      {pixData && (
        <div style={{ marginTop: 20 }}>
          <h4>PIX Gerado</h4>
          <img src={`data:image/png;base64,${pixData.qr_code_base64}`} alt="QR Code PIX" width={250} height={250} />
          <input type="text" value={pixData.qr_code} readOnly style={{ width: "100%", padding: 8, marginTop: 10 }} />
          <a href={pixData.ticket_url} target="_blank" rel="noopener noreferrer">Abrir PIX em nova aba</a>
        </div>
      )}

      {status && <p style={{ color: "green" }}>{status}</p>}
      {error && <p style={{ color: "red" }}>{error}</p>}
    </div>
  );
}
